import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Truck,
  FileText,
  DollarSign,
  UserCircle,
  ChevronRight,
  HelpCircle,
  MessageCircle,
  BarChart2,
  BookOpen,
} from "lucide-react";
import { Button, Input, Card, CardContent, Skeleton } from "@musa.tech/design-system";
import { usePoliticasContent, PoliticaContent } from "@/hooks/usePoliticasContent";
import { getCategoriaSlug, generateSlug } from "@/utils/slugUtils";
import { useAnalytics } from "@/hooks/useAnalytics";
import { openCloudHumansChat } from "@/utils/cloudHumans";

// Mapeamento de categoria UI → PoliticaContentType
const CATEGORIAS = [
  {
    slug: "coletas",
    label: "Coletas",
    descricao: "Coletas recorrentes, sob demanda e status.",
    icon: Truck,
    tipo: "Coletas" as const,
    itensFallback: [
      "A coleta já aconteceu?",
      "Quando será minha próxima coleta?",
      "Minha coleta não aconteceu",
    ],
  },
  {
    slug: "documentos",
    label: "Documentos",
    descricao: "MTR, evidências e comprovantes.",
    icon: FileText,
    tipo: "Documentação" as const,
    itensFallback: [
      "Como baixar MTR",
      "Onde encontrar evidências de coleta",
      "Como acessar manifestos",
    ],
  },
  {
    slug: "financeiro",
    label: "Financeiro",
    descricao: "Consulta de faturas e previsão de cobrança.",
    icon: DollarSign,
    tipo: "Financeiro" as const,
    itensFallback: [
      "Como consultar faturas",
      "Previsão da fatura do mês",
      "Histórico de cobrança",
    ],
  },
  {
    slug: "conta-e-acesso",
    label: "Conta e acesso",
    descricao: "Login, usuários e permissões.",
    icon: UserCircle,
    tipo: "Usuários e permissões" as const,
    itensFallback: [
      "Problemas de login",
      "Alterar usuários",
      "Permissões da conta",
    ],
  },
  {
    slug: "dados",
    label: "Dados",
    descricao: "Dashboard, métricas de impacto e relatórios.",
    icon: BarChart2,
    tipo: "Dados" as const,
    itensFallback: [
      "Como usar o dashboard",
      "Dados de impacto",
      "Pesquisa de satisfação (CSAT)",
    ],
  },
  {
    slug: "geral",
    label: "Geral",
    descricao: "Informações gerais sobre o Portal do Gerador.",
    icon: BookOpen,
    tipo: "Geral" as const,
    itensFallback: [
      "O que é o Portal do Gerador",
      "Destinação de resíduos",
      "Classificação de resíduos",
    ],
  },
] as const;

const CentralDeAjuda = () => {
  const navigate = useNavigate();
  const { politicas, isLoading } = usePoliticasContent();
  const { trackButtonClick, trackModalOpen } = useAnalytics();
  const [busca, setBusca] = useState("");

  /**
   * Os três gatilhos de abrir chamado (topo, rodapé e link do estado sem
   * resultado) registram a mesma abertura — a intenção do usuário é idêntica
   * (GR-712, Suposição 10).
   */
  const handleAbrirChamado = () => {
    trackModalOpen({ modalName: 'Abrir Chamado' });
    openCloudHumansChat();
  };

  const navigateToArtigo = (article: PoliticaContent, searchTerm?: string) => {
    const categoria = getCategoriaSlug(article.type) ?? 'coletas';
    const slug = generateSlug(article.title);
    // `categoria` e `titulo_artigo` vão como parâmetros adicionais do evento, não
    // embutidos no nome — permite filtrar por categoria no GA4 sem precisar de um
    // nome de evento por artigo (GR-712, RN-11). A categoria enviada é o rótulo
    // visível, não o valor interno de classificação (Suposição 01).
    trackButtonClick({
      buttonLabel: 'central_ajuda_visualizar_artigo',
      categoria: CATEGORIAS.find((c) => c.slug === categoria)?.label ?? categoria,
      titulo_artigo: article.title,
    });
    const params = new URLSearchParams({ artigo: slug });
    if (searchTerm) params.set('q', searchTerm);
    navigate(`/ajuda/${categoria}?${params.toString()}`);
  };

  const visiveis = useMemo(
    () => (politicas ?? []).filter((p) => p.is_visible),
    [politicas],
  );

  const resultadosBusca = useMemo(() => {
    if (!busca.trim()) return [];
    const termo = busca.toLowerCase();
    return visiveis.filter((p) => p.title.toLowerCase().includes(termo));
  }, [busca, visiveis]);

  const perguntasFrequentes = useMemo(
    () => visiveis.slice(0, 4),
    [visiveis],
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-4xl mx-auto space-y-8 min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
              Central de Ajuda
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Encontre respostas para suas dúvidas
            </p>
          </div>
          <Button
            variant="default"
            className="gap-2 self-start sm:self-auto"
            onClick={handleAbrirChamado}
          >
            <MessageCircle className="h-4 w-4" />
            Abrir chamado
          </Button>
        </div>

        {/* Campo de busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <Input
            placeholder="Buscar artigos e perguntas frequentes..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9 h-11 border-neutral-200 focus-visible:ring-primary-500"
          />
        </div>

        {/* Resultados de busca */}
        {busca.trim() && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-neutral-700">
              Resultados para &ldquo;{busca}&rdquo;
            </h2>
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : resultadosBusca.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4">
                Nenhum artigo encontrado. Tente outros termos ou{" "}
                <button
                  type="button"
                  onClick={handleAbrirChamado}
                  className="text-primary-600 hover:underline font-medium"
                >
                  abra um chamado
                </button>
                .
              </p>
            ) : (
              <div className="space-y-1">
                {resultadosBusca.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => navigateToArtigo(p, busca.trim())}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-neutral-200 bg-white hover:bg-primary-50 hover:border-primary-300 text-left transition-colors"
                  >
                    <span className="text-sm font-medium text-neutral-800">
                      {p.title}
                    </span>
                    <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Categorias de ajuda */}
        {!busca.trim() && (
          <>
            <section className="space-y-4">
              <h2 className="text-base font-semibold text-neutral-900">
                Categorias de ajuda
              </h2>
              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-36 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {CATEGORIAS.map((cat) => {
                    const Icon = cat.icon;
                    const itensDoTipo = visiveis
                      .filter((p) => p.type === cat.tipo)
                      .slice(0, 3);
                    const itensExibidos =
                      itensDoTipo.length > 0
                        ? itensDoTipo.map((p) => p.title)
                        : cat.itensFallback;
                    const totalDoTipo = visiveis.filter(
                      (p) => p.type === cat.tipo,
                    ).length;

                    return (
                      <Card
                        key={cat.slug}
                        data-testid="central-ajuda-card-categoria"
                        className="cursor-pointer border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                        onClick={() => navigate(`/ajuda/${cat.slug}`)}
                      >
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-100">
                              <Icon className="h-4 w-4 text-primary-600" />
                            </div>
                            <span className="font-semibold text-sm text-neutral-900">
                              {cat.label}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500">
                            {cat.descricao}
                          </p>
                          <ul className="space-y-1">
                            {itensExibidos.map((titulo, idx) => (
                              <li
                                key={idx}
                                className="text-xs text-neutral-700 flex items-center gap-1.5"
                              >
                                <span className="w-1 h-1 rounded-full bg-primary-400 flex-shrink-0" />
                                {titulo}
                              </li>
                            ))}
                            {totalDoTipo > 3 && (
                              <li className="text-xs text-primary-600 font-medium">
                                +{totalDoTipo - 3} artigos
                              </li>
                            )}
                          </ul>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Perguntas frequentes */}
            <section className="space-y-4">
              <h2 className="text-base font-semibold text-neutral-900">
                Perguntas frequentes
              </h2>
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-lg" />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {perguntasFrequentes.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => navigateToArtigo(p)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-neutral-200 bg-white hover:bg-primary-50 hover:border-primary-300 text-left transition-colors"
                    >
                      <span className="text-sm font-medium text-neutral-800">
                        {p.title}
                      </span>
                      <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* CTA inferior — Abrir chamado */}
            <section className="rounded-xl border border-neutral-200 bg-neutral-50 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <HelpCircle className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    Não encontrou o que precisava?
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Nossa equipe de suporte está pronta para ajudar.
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                className="gap-2 flex-shrink-0"
                onClick={handleAbrirChamado}
              >
                <MessageCircle className="h-4 w-4" />
                Abrir chamado
              </Button>
            </section>
          </>
        )}
      </div>

    </div>
  );
};

export default CentralDeAjuda;
